import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import cx from 'classnames';
import moment from 'moment';
import toast from 'react-hot-toast';
import { MessageSquare, X, CheckCircle, Trash2, Plus } from 'lucide-react';
import {
  useFetchPartnerPropertiesQuery,
  useFetchPropertyQuery,
} from '@@/services/property';
import { useCreateMessageMutation } from '@@/services/message';
import { useFieldArray, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import StandardInput from '@@/components/Inputs/StandardInput';
import MultiSelectInput from '@@/components/Inputs/MultiSelectInput';
import CheckboxInput from '@@/components/Inputs/CheckboxInput';
import RadioInput from '@@/components/Inputs/RadioInput';
import DatePickerInput from '@@/components/Inputs/DatePickerInput';
import { selectResidentTypes } from '@@/constants/user';

const StepLabel: React.FC<{
  rank: number;
  label: string;
  step: number;
  setStep: (step: number) => void;
}> = ({ rank, label, step, setStep }) => {
  const active = useMemo(() => step === rank, [step, rank]);
  const done = useMemo(() => step > rank, [step, rank]);

  return (
    <div
      onClick={() => {
        if (done) {
          setStep(rank);
        }
      }}
      className={cx(
        'flex items-center px-4 py-3 rounded-lg transition-all duration-200 my-2',
        active && 'bg-primary-600 shadow-md',
        done && 'cursor-pointer hover:bg-neutral-100',
        !active && !done && 'opacity-60',
      )}
    >
      <div
        className={cx(
          'h-10 w-10 rounded-full text-white font-semibold flex items-center justify-center transition-colors',
          active && 'bg-white text-primary-600',
          done && 'bg-success-500',
          !active && !done && 'bg-neutral-400',
        )}
      >
        {done ? <CheckCircle size={20} /> : rank}
      </div>
      <p className={cx('ml-3 font-medium text-body', active ? 'text-white' : 'text-neutral-900')}>{label}</p>
    </div>
  );
};

const schema = yup
  .object({
    sendToAllProperties: yup.boolean(),
    buildingsFilter: yup.array(
      yup.object({
        id: yup.string(),
        label: yup.string(),
      }),
    ),
    floorsFilter: yup.array(
      yup.object({
        id: yup.string(),
        label: yup.string(),
      }),
    ),
    residentTypesFilter: yup.array(
      yup.object({
        id: yup.string(),
        label: yup.string(),
      }),
    ),
    isMemberOfCondominiumCouncil: yup.boolean(),
    message: yup
      .string()
      .max(800, 'Le message ne doit pas dépasser 800 caractères'),
    messageType: yup.string(),
    postTitle: yup
      .string()
      .max(100, 'Le titre ne doit pas dépasser 100 caractères'),
    postDescription: yup
      .string()
      .max(800, 'La description ne doit pas dépasser 800 caractères'),
    postOptions: yup.array(
      yup.object({
        id: yup.string(),
        value: yup.string(),
      }),
    ),
    postAuthorizeCustomOptions: yup.boolean(),
    postAuthorizeMultipleChoices: yup.boolean(),
    scheduledAt: yup.date().required('Veuillez sélectionner une date'),
  })
  .required();

const NewMessagePage = () => {
  const navigation = useNavigate();
  const [step, setStep] = useState(1);
  const [file, setFile] = useState<File | undefined>(undefined);
  const [createMessage, { isSuccess }] = useCreateMessageMutation();

  const {
    register,
    control,
    watch,
    reset,
    getValues,
    formState: { errors },
  } = useForm<{
    sendToAllProperties: boolean;
    propertiesFilter: {
      id: string;
      label: string;
    };
    buildingsFilter?: { id: string; label: string }[];
    floorsFilter?: { id: string; label: string }[];
    residentTypesFilter?: { id: string; label: string }[];
    isMemberOfCondominiumCouncil: boolean;
    messageType: string;
    message?: string;
    postTitle?: string;
    postDescription?: string;
    postOptions?: { id?: string; value?: string }[];
    postAuthorizeCustomOptions: boolean;
    postAuthorizeMultipleChoices: boolean;
    scheduledAt: Date;
  }>({
    resolver: yupResolver(schema),
    mode: 'onBlur',
    defaultValues: { messageType: 'message' },
  });

  useEffect(() => {
    if (isSuccess) {
      navigation(-1);
      reset();
      toast.success('Le message a bien été créé', {
        position: 'top-right',
      });
    }
  }, [isSuccess, navigation, reset]);

  const handleFile = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files?.length) {
        setFile(event.target.files[0]);
      }
    },
    [setFile],
  );

  const [
    sendToAllProperties,
    propertiesFilter,
    buildingsFilter,
    floorsFilter,
    residentTypesFilter,
    isMemberOfCondominiumCouncil,
    messageTxt,
    messageType,
    postTitle,
    postDescription,
    scheduledAt,
  ] = watch([
    'sendToAllProperties',
    'propertiesFilter',
    'buildingsFilter',
    'floorsFilter',
    'residentTypesFilter',
    'isMemberOfCondominiumCouncil',
    'message',
    'messageType',
    'postTitle',
    'postDescription',
    'scheduledAt',
  ]);

  const {
    fields: postOptions,
    append: appendPostOption,
    remove: removePostOption,
  } = useFieldArray({
    control,
    // @ts-ignore
    name: 'postOptions',
  });

  const { data: properties } = useFetchPartnerPropertiesQuery({
    limit: 1000,
    page: 1,
  });

  const { data: selectedProperty } = useFetchPropertyQuery(
    propertiesFilter?.id,
    {
      skip: !propertiesFilter?.id,
    },
  );

  const selectProperty = useMemo(() => {
    if (sendToAllProperties) {
      return null;
    }

    return (
      <MultiSelectInput
        control={control}
        id="propertiesFilter"
        label="Choix d'une résidence (*)"
        options={
          properties?.docs?.map((p) => ({
            id: p.id,
            label: p.name,
          })) ?? []
        }
        isMulti={false}
      />
    );
  }, [sendToAllProperties, control, properties]);

  const step1 = useMemo(() => {
    return (
      <>
        <p className="my-6 text-main font-main">
          SÉLECTIONNEZ LES RÉSIDENCES CONCERNÉES
        </p>

        <CheckboxInput
          register={register}
          id="sendToAllProperties"
          label="Envoyer à toutes les résidences ?"
        />

        <div className="my-6" />
        {selectProperty}

        {!sendToAllProperties && <div className="my-4" />}
        {!sendToAllProperties && (
          <MultiSelectInput
            control={control}
            id="buildingsFilter"
            label="Choix des bâtiments"
            options={
              selectedProperty
                ? selectedProperty.buildings.map((b: Alacaza.Building) => ({
                    id: b.buildingId,
                    label: b.name,
                  }))
                : []
            }
          />
        )}

        {!sendToAllProperties && <div className="my-4" />}
        {!sendToAllProperties && (
          <MultiSelectInput
            control={control}
            id="floorsFilter"
            label="Choix des étages"
            options={[...Array(100).keys()].map((floor: number) => ({
              id: floor.toString(),
              label: floor.toString(),
            }))}
          />
        )}

        <div className="my-4" />
        <MultiSelectInput
          control={control}
          id="residentTypesFilter"
          label="Typologie des résidents"
          options={selectResidentTypes}
        />

        <div className="mt-4" />
        <CheckboxInput
          register={register}
          id="isMemberOfCondominiumCouncil"
          label="Seulement les membres du conseil syndical ?"
        />
      </>
    );
  }, [register, control, selectProperty, selectedProperty]);

  const step2Message = useMemo(() => {
    return (
      <div>
        <p className="italic text-sm text-end mb-0.5">
          {messageTxt?.length || 0}/800
        </p>
        <StandardInput
          register={register}
          id="message"
          label="Message *"
          type="textarea"
          placeholder="Message"
          error={errors.message}
        />

        <div className="mt-8">
          <p className="text-body-sm font-medium text-neutral-900 mb-2">Pièce jointe</p>
          <p className="text-body-xs text-neutral-500 mb-3">
            jpg, .jpeg, .gif, .png, .pdf, .doc, .xls, .ppt
          </p>

          {file ? (
            <div className="mt-3 border border-neutral-200 rounded-lg px-4 py-3 flex flex-row justify-between items-center bg-neutral-50">
              <p className="text-body-sm text-neutral-900 truncate">{file.name}</p>
              <button
                onClick={() => setFile(undefined)}
                className="ml-4 text-danger-600 hover:text-danger-700 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          ) : (
            <input
              type="file"
              accept=".jpg,.jpeg,.gif,.png,.pdf,.doc,.xls,.ppt"
              onChange={handleFile}
              className="mt-3 text-body-sm text-neutral-700 file:mr-4 file:py-2 file:px-4 file:rounded-button file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 file:cursor-pointer"
            />
          )}
        </div>
      </div>
    );
  }, [file, handleFile, messageTxt, register, errors.message]);

  const step2Poll = useMemo(() => {
    return (
      <div>
        <p className="italic text-sm text-end mb-0.5">
          {postTitle?.length || 0}/100
        </p>
        <StandardInput
          register={register}
          id="postTitle"
          label="Titre du sondage *"
          type="text"
          placeholder="Titre du sondage"
          error={errors.postTitle}
        />

        <div className="my-8" />

        <p className="italic text-sm text-end mb-0.5">
          {postDescription?.length || 0}/800
        </p>
        <StandardInput
          register={register}
          id="postDescription"
          label="Description du sondage *"
          type="textarea"
          placeholder="Description du sondage"
          error={errors.postDescription}
        />

        <div className="mt-8">
          <div className="flex flex-row items-center justify-between mb-6">
            <p className="text-body-sm font-medium text-neutral-900">Options :</p>
            <button
              type="button"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-button transition-colors shadow-button"
              onClick={() => appendPostOption({})}
            >
              <Plus size={16} />
              <span className="text-body-xs font-medium">Ajouter une option</span>
            </button>
          </div>

          <ul className="flex flex-col space-y-6 mb-8">
            {postOptions.map((field, index) => (
              <div key={index} className="flex flex-row items-end space-x-3">
                <div className="flex-1">
                  <StandardInput
                    register={register}
                    id={`postOptions.${index}.value`}
                    label={`Option ${index + 1}`}
                    placeholder={`Disponibilité ${index + 1}`}
                    type="text"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removePostOption(index)}
                  className="p-3 text-danger-600 hover:text-danger-700 hover:bg-danger-50 rounded-button transition-colors"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            ))}
          </ul>
        </div>

        <div className="my-6 space-y-4">
          <CheckboxInput
            register={register}
            id="postAuthorizeCustomOptions"
            label="Autoriser des réponses différentes à celles que je propose ?"
          />
          <CheckboxInput
            register={register}
            id="postAuthorizeMultipleChoices"
            label="Autoriser plusieurs réponses par personne ?"
          />
        </div>

        <div className="mt-8">
          <p className="text-body-sm font-medium text-neutral-900 mb-2">Image</p>
          <p className="text-body-xs text-neutral-500 mb-3">jpg, .jpeg, .gif, .png</p>
          {file ? (
            <div className="mt-3 border border-neutral-200 rounded-lg px-4 py-3 flex flex-row justify-between items-center bg-neutral-50">
              <p className="text-body-sm text-neutral-900 truncate">{file.name}</p>
              <button
                onClick={() => setFile(undefined)}
                className="ml-4 text-danger-600 hover:text-danger-700 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          ) : (
            <input
              type="file"
              accept=".jpg,.jpeg,.gif,.png"
              onChange={handleFile}
              className="mt-3 text-body-sm text-neutral-700 file:mr-4 file:py-2 file:px-4 file:rounded-button file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 file:cursor-pointer"
            />
          )}
        </div>
      </div>
    );
  }, [
    postTitle,
    register,
    errors.postTitle,
    postDescription,
    errors.postDescription,
    file,
    setFile,
    postOptions,
    appendPostOption,
    removePostOption,
  ]);

  const step2 = useMemo(() => {
    return (
      <div className="font-main text-main">
        <RadioInput
          control={control}
          id="messageType"
          options={[
            { id: 'message', label: 'Message Standard' },
            { id: 'poll', label: 'Sondage' },
          ]}
        />
        <div className="my-4" />
        {messageType === 'message' && step2Message}
        {messageType === 'poll' && step2Poll}
      </div>
    );
  }, [control, step2Message, step2Poll, messageType]);

  const step3 = useMemo(() => {
    return (
      <DatePickerInput
        id="scheduledAt"
        control={control}
        showTimeSelect
        label="Date d'envoi *"
      />
    );
  }, []);

  const step4 = useMemo(() => {
    const isPoll = messageType === 'poll';
    return (
      <div>
        <h3 className="text-heading-md text-neutral-900 mb-4">Récapitulatif</h3>
        <div className="border-t border-neutral-200 pt-4 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <p className="font-semibold text-body text-neutral-900">Résidences concernées</p>
            <p className="text-body-sm text-neutral-600">
              {sendToAllProperties
                ? 'Toutes les résidences'
                : selectedProperty?.name}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <p className="font-semibold text-body text-neutral-900">Bâtiments concernés</p>
            <p className="text-body-sm text-neutral-600">
              {buildingsFilter?.map((b) => b.label).join(', ') ||
                'Tous les bâtiments'}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <p className="font-semibold text-body text-neutral-900">Etages concernés</p>
            <p className="text-body-sm text-neutral-600">
              {floorsFilter?.map((b) => b.label).join(', ') || 'Tous les étages'}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <p className="font-semibold text-body text-neutral-900">Résidents concernés</p>
            <p className="text-body-sm text-neutral-600">
              {residentTypesFilter?.map((b) => b.label).join(', ') ||
                'Tous les résidents'}
            </p>
          </div>

          {isMemberOfCondominiumCouncil && (
            <div className="inline-flex items-center px-3 py-1.5 bg-warning-100 text-warning-800 rounded-badge text-body-xs font-medium ml-auto">
              Membre du conseil syndical uniquement
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <p className="font-semibold text-body text-neutral-900">Date d'envoi</p>
            <p className="text-body-sm text-neutral-600">
              {moment(scheduledAt).format('lll')}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row sm:justify-between gap-2">
            <p className="font-semibold text-body text-neutral-900">{isPoll ? 'Sondage' : 'Message'}</p>
            <p className="text-body-sm text-neutral-600 break-words sm:max-w-md">
              {isPoll ? postTitle : messageTxt}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <p className="font-semibold text-body text-neutral-900">Pièce jointe</p>
            <p className="text-body-sm text-neutral-600">{file?.name || 'Aucune'}</p>
          </div>
        </div>
      </div>
    );
  }, [
    sendToAllProperties,
    selectedProperty,
    buildingsFilter,
    floorsFilter,
    residentTypesFilter,
    isMemberOfCondominiumCouncil,
    messageType,
    postTitle,
    messageTxt,
    scheduledAt,
    file,
  ]);

  const getStep = useCallback(() => {
    switch (step) {
      case 1:
        return step1;
      case 2:
        return step2;
      case 3:
        return step3;
      case 4:
        return step4;

      default:
        return null;
    }
  }, [step, step1, step2, step3, step4]);

  const next = useCallback(async () => {
    const form = getValues();
    switch (step) {
      case 1: {
        if (!form.sendToAllProperties && !selectedProperty) {
          toast.error('Veuillez sélectionner une résidence', {
            position: 'top-right',
          });
          return;
        }

        setStep((step) => step + 1);
        break;
      }

      case 2: {
        switch (messageType) {
          case 'message': {
            if (!form.message) {
              toast.error('Veuillez rédiger un message', {
                position: 'top-right',
              });
              return;
            }

            break;
          }

          case 'poll': {
            if (
              !form.postTitle ||
              !form.postDescription ||
              !form.postOptions?.length ||
              form.postOptions?.length < 2
            ) {
              toast.error(
                'Veuillez indiquer un titre, une description et au moins deux options',
                {
                  position: 'top-right',
                },
              );
              return;
            }

            break;
          }

          default: {
            break;
          }
        }

        setStep((step) => step + 1);
        break;
      }

      case 3: {
        if (!form.scheduledAt) {
          toast.error("Veuillez sélectionner une date d'envoi", {
            position: 'top-right',
          });
          return;
        }

        setStep((step) => step + 1);
        break;
      }

      case 4: {
        await createMessage({
          message: form.message,
          scheduledAt: form.scheduledAt,
          sendToAllProperties: form.sendToAllProperties,
          propertyId: selectedProperty?.id,
          buildings: form.buildingsFilter?.map((b) => b.id),
          floors: form.floorsFilter?.map((f) => f.id),
          residentTypes: form.residentTypesFilter?.map((r) => r.id),
          isMemberOfCondominiumCouncil: form.isMemberOfCondominiumCouncil,
          attachment: file,
          post:
            messageType === 'poll'
              ? {
                  title: form.postTitle!,
                  body: form.postDescription!,
                  pollOptions: form.postOptions!.map((o) => ({
                    label: o.value!,
                  })),
                  authorizeCustomOptions: !!form.postAuthorizeCustomOptions,
                  authorizeMultipleChoices: !!form.postAuthorizeMultipleChoices,
                  images: file ? [file] : [],
                }
              : undefined,
        });
      }

      default: {
        break;
      }
    }
  }, [step, selectedProperty, createMessage, getValues, file, messageType]);

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="px-page-sm py-6 lg:px-page lg:py-8 w-full">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center">
              <MessageSquare size={28} className="text-neutral-900" />
              <h1 className="text-heading-lg text-neutral-900 ml-3">Nouveau Message</h1>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-6">
            {/* Stepper Sidebar */}
            <div className="lg:w-1/3 max-w-md">
              <div className="bg-white rounded-card shadow-card border border-neutral-200 p-4">
                <StepLabel
                  rank={1}
                  label="Résidences"
                  step={step}
                  setStep={setStep}
                />
                <StepLabel rank={2} label="Message" step={step} setStep={setStep} />
                <StepLabel
                  rank={3}
                  label="Date d'envoi"
                  step={step}
                  setStep={setStep}
                />
                <StepLabel
                  rank={4}
                  label="Validation"
                  step={step}
                  setStep={setStep}
                />
              </div>
            </div>

            {/* Form Content */}
            <div className="flex-1">
              <div className="bg-white rounded-card shadow-card border border-neutral-200 p-6">
                <form>
                  {getStep()}
                  <div className="mt-8 pt-6 border-t border-neutral-200 flex justify-end">
                    <button
                      type="button"
                      className="inline-flex items-center px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-button shadow-button transition-colors duration-200"
                      onClick={next}
                    >
                      {step === 4 ? 'Valider et créer' : 'Continuer'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewMessagePage;
